import { useQuery } from '@tanstack/react-query';

import { apiFetch, ApiRequestError } from '@/lib/api/client';
import { runtimeConfig } from '@/lib/config';

/**
 * 사내 주소록의 조직 트리 — front `lib/directory/useDirectory.ts` 에서 트리만 옮겼다.
 *
 * 안전 서버의 조직 사본(`/v1/safe/lookup/directory` · DOC-141 §11-3)을 읽는다 — 라운지 결재 조직 트리를 펴 보낸 것이라
 * 계정 있는 재직자만 나오고, 전화 · 메일 · 사진 · 업무는 건너오지 않는다(phone · email = null · hasPhoto = false · matrixDuties = []).
 */

export type DirectoryMember = {
  personnelId: string;
  name: string;
  rank: string | null;
  position: string | null;
  /** 안전 서버는 늘 null 이다(전화는 건너오지 않는다). 화면에 그리지 않는다. */
  phone: string | null;
  teamLead: boolean;
  hasPhoto: boolean;
  email?: string | null;
  duties?: string | null;
  matrixDuties?: string[];
};

export type DirectoryUnit = {
  /** DEPARTMENT | SITE */
  unitType: string;
  unitName: string;
  members: DirectoryMember[];
};

/** 조직도 트리. 인사 배치는 하루에 몇 번씩 바뀌는 자료가 아니라 오래 신선한 것으로 본다. */
export function useDirectoryTree(enabled: boolean) {
  return useQuery({
    queryKey: ['directory', 'tree'] as const,
    queryFn: () => fetchTree(),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** 서버에 닿지 못했고 개발 중일 때만 빈 트리로 물러선다 — 세션과 같은 판정이다. */
async function fetchTree(): Promise<DirectoryUnit[]> {
  try {
    return await apiFetch<DirectoryUnit[]>('/v1/safe/lookup/directory');
  } catch (error) {
    const unreachable =
      error instanceof TypeError || (error instanceof ApiRequestError && error.apiError === null);
    if (unreachable && runtimeConfig().mockSessionWhenOffline) return [];
    throw error;
  }
}
